import { useEffect, useMemo, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { changeLanguage } from '@app/i18n';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TranslateIcon from '@mui/icons-material/Translate';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

const STORAGE_KEY = 'bennie.i18n.locale';

const locales = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const I18nToggle = () => {
  const flags = useFlags();
  const translationsEnabled = Boolean(flags?.translations);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const open = Boolean(anchorEl);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleClose = () => {
    clearCloseTimer();
    setAnchorEl(null);
  };

  const scheduleMenuClose = () => {
    if (!open) {
      return;
    }

    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      handleClose();
    }, 1000);
  };

  const savedLocale = useMemo(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!translationsEnabled) {
      changeLanguage('en');
      try {
        localStorage.setItem(STORAGE_KEY, 'en');
      } catch (_) {
        // noop
      }
      return;
    }

    if (savedLocale) {
      changeLanguage(savedLocale);
    }
  }, [savedLocale, translationsEnabled]);

  useEffect(() => {
    if (open) {
      setTooltipOpen(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, []);

  const handleTooltipOpen = () => {
    if (!open) {
      setTooltipOpen(true);
    }
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleOpen = event => {
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = code => {
    changeLanguage(code).finally(() => {
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch (_) {
        // noop
      }
      handleClose();
    });
  };

  const availableLocales = useMemo(() => {
    if (!translationsEnabled) {
      return locales.filter(locale => locale.code === 'en');
    }
    return locales;
  }, [translationsEnabled]);

  if (availableLocales.length <= 1) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 8,
        right: 8,
        zIndex: 2000,
      }}
    >
      <Tooltip
        title="Switch language"
        open={tooltipOpen}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
        disableFocusListener={open}
        disableHoverListener={open}
        disableTouchListener={open}
      >
        <IconButton
          aria-label="Switch language"
          color="primary"
          size="small"
          onClick={handleOpen}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleMenuClose}
        >
          <TranslateIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          onMouseEnter: clearCloseTimer,
          onMouseLeave: scheduleMenuClose,
        }}
        PaperProps={{
          onMouseEnter: clearCloseTimer,
          onMouseLeave: scheduleMenuClose,
        }}
      >
        {availableLocales.map(locale => (
          <MenuItem key={locale.code} onClick={() => handleSelect(locale.code)}>
            {locale.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default I18nToggle;


